/**
 * @title Public Page
 * @description The frontpage of the retrade app
 * @image https://storageaccountourimbea2.blob.core.windows.net/tradein2guide/easybox.png
 */

import React, {FC} from "react";
import {GlobalLayout, StyledDiv} from "../../providers/layout/LayoutProvider";
import clsx from "clsx";
import {List, ListItem, ListItemAvatar, ListItemText, Theme} from "@mui/material";
import {makeStyles} from "@mui/styles";
import {useRecoilValue} from "recoil";
import {DropOffIcon, PackItIcon, TradeInIcon} from "../../svg/SvgIcons";
import {LocalizationsAtom} from "../../providers/localization/LocalizationProvider";
import {LocalizationText} from "../../providers/localization/LocalizationText";

export const PublicRenderer: FC<any> = ({children}) => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const classes = useStyles();

   let style: any;
   if (localizations.publicPageBackgroundImage) {
      style = {
         backgroundImage: 'url(' + localizations.publicPageBackgroundImage + ')',
         backgroundSize: 'cover',
         backgroundRepeat: 'no-repeat',
         backgroundPosition: 'center center'
      }
   }

   return <div className={"c2s-publicpage"}>
      <StyledDiv type={"front"} className={clsx(classes.container, "c2s-frontpage")}>
         <StyledDiv type={"front"} className={classes.item} style={style}>
            <div className={classes.desktopPadding}>
               <div className={clsx(classes.leftPanel, "c2s-publicpage-login")}>
                  {children}
               </div>
            </div>
         </StyledDiv>
         <StyledDiv type={"contrast"} className={classes.item}>
            <div className={clsx(classes.desktopPadding, classes.rightPanel)}>
               <EasyBox/>
            </div>
         </StyledDiv>
      </StyledDiv>
      <div className={clsx(classes.videoContainer, "video-container")}>
         {(localizations.publicPageFooter && localizations.publicPageFooter.length !== 0) &&
            <div dangerouslySetInnerHTML={{__html: localizations.publicPageFooter}}/>
         }
      </div>

   </div>
}


const useStyles = makeStyles({
   container: {
      display: 'flex',
      width: '100%',
      flexDirection: 'row',
      alignItems: 'top',
   },
   item: {
      flexGrow: 1,
      width: '100%',
   },
   desktopPadding: {
      padding: '20px 50px 20px 50px',
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      marginTop: '20px',
      marginBottom: '20px'
   },
   leftPanel: {
      textAlign: 'left',
      gap: '15px',
      display: 'flex',
      flexDirection: 'column'
   },
   rightPanel: {
      gap: '15px'
   },
   '@media only screen and (max-width: 800px)': {
      container: {
         flexDirection: 'column',
      } as any,
      desktopPadding: {
         padding: '20px'
      }
   },
   '@media only screen and (max-width: 1000px)': {
      desktopPadding: {
         padding: '20px'
      },
   },
   videoContainer: {
      padding: '40px',
      marginTop: '40px'
   }
});

export const EasyBox = () => {
   const localizations = useRecoilValue(LocalizationsAtom);
   const globalLayout = useRecoilValue(GlobalLayout);
   const classes = useEasyStyles();
   return <div className={"c2s-easybox-container"}>
      <div className={"c2s-easybox-title"}>
         <LocalizationText
            text={localizations.easyBoxTitle}
            typographyProps={{
               variant: "h1",
               type: "contrast",
               className: classes.centerText
            }}
         />
      </div>
      <List className={clsx(classes.list, "c2s-easybox-list")}>
         <ListItem className={classes.listItem} sx={{paddingTop: 0}}>
            <ListItemText>
            </ListItemText>
         </ListItem>
         <ListItem className={classes.listItem}>
            {!globalLayout.disableIcons && <ListItemAvatar className={classes.listAvatar}>
               {localizations.easyBoxSection1Icon ?
                  <img src={localizations.easyBoxSection1Icon} style={{maxWidth: '45px', maxHeight: '45px'}}
                       alt={"Step 4"}/>
                  :
                  <TradeInIcon type={"contrast"}/>
               }
            </ListItemAvatar>}
            <ListItemText className={classes.listText}>
               <LocalizationText
                  text={localizations.easyBoxSection1Title}
                  typographyProps={{
                     variant: "h2",
                     type: "contrast",
                     className: classes.text
                  }}
               />
               <LocalizationText
                  text={localizations.easyBoxSection1Description}
                  typographyProps={{
                     variant: "body1",
                     type: "contrast",
                  }}
               />
            </ListItemText>
         </ListItem>
         <ListItem className={classes.listItem}>
            {!globalLayout.disableIcons && <ListItemAvatar className={classes.listAvatar}>
               {localizations.easyBoxSection2Icon ?
                  <img src={localizations.easyBoxSection2Icon} style={{maxWidth: '45px', maxHeight: '45px'}}
                       alt={"Step 4"}/>
                  :
                  <PackItIcon type={"contrast"}/>
               }
            </ListItemAvatar>}
            <ListItemText className={classes.listText}>
               <LocalizationText
                  text={localizations.easyBoxSection2Title}
                  typographyProps={{
                     variant: "h2",
                     type: "contrast",
                     className: classes.text
                  }}
               />
               <LocalizationText
                  text={localizations.easyBoxSection2Description}
                  typographyProps={{
                     variant: "body1",
                     type: "contrast",
                  }}
               />
            </ListItemText>
         </ListItem>
         <ListItem className={classes.listItem} sx={{paddingTop: 0}}>
            {!globalLayout.disableIcons && <ListItemAvatar className={classes.listAvatar}>
               {localizations.easyBoxSection3Icon ?
                  <img src={localizations.easyBoxSection3Icon} style={{maxWidth: '45px', maxHeight: '45px'}}
                       alt={"Step 4"}/>
                  :
                  <DropOffIcon type={"contrast"}/>
               }
            </ListItemAvatar>}
            <ListItemText className={classes.listText}>
               <LocalizationText
                  text={localizations.easyBoxSection3Title}
                  typographyProps={{
                     variant: "h2",
                     type: "contrast",
                     className: classes.text
                  }}
               />
               <LocalizationText
                  text={localizations.easyBoxSection3Description}
                  typographyProps={{
                     variant: "body1",
                     type: "contrast",
                  }}
               />
            </ListItemText>
         </ListItem>
         {localizations.easyBoxSection4Icon && <ListItem className={classes.listItem} sx={{paddingTop: 0}}>
            {!globalLayout.disableIcons && <ListItemAvatar className={classes.listAvatar}>
               <img src={localizations.easyBoxSection4Icon} style={{maxWidth: '45px', maxHeight: '45px'}}
                    alt={"Step 4"}/>
            </ListItemAvatar>}
            <ListItemText className={classes.listText}>
               <LocalizationText
                  text={localizations.easyBoxSection4Title}
                  typographyProps={{
                     variant: "h2",
                     type: "contrast",
                     className: classes.text
                  }}
               />
               <LocalizationText
                  text={localizations.easyBoxSection4Description}
                  typographyProps={{
                     variant: "body1",
                     type: "contrast",
                  }}
               />
            </ListItemText>
         </ListItem>}
         {(localizations.easyBoxSignature && localizations.easyBoxSignature.length) &&
            <ListItem className={classes.listItem} sx={{paddingTop: 0}}>
               <ListItemText className={classes.listText}>
                  <div className={clsx(classes.signatureText, "c2s-signature-container")}>
                     {(localizations.easyBoxSignature && localizations.easyBoxSignature.length !== 0) &&
                        <div dangerouslySetInnerHTML={{__html: localizations.easyBoxSignature}}/>
                     }
                  </div>
               </ListItemText>
            </ListItem>}
      </List>
   </div>
}


const useEasyStyles = makeStyles((theme: Theme) => ({
      centerText: {},
      text: {
         marginBottom: '10px'
      },
      list: {
         alignItems: 'top',
         paddingTop: '0'
      },
      listItem: {
         alignItems: 'start',
         marginBottom: '30px',
         gap: '30px',
      },
      signatureText: {},
      listAvatar: {},
      listText: {
         marginTop: '0'
      },
      '@media only screen and (max-width: 800px)': {
         listItem: {
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '600px',
            gap: '10px'
         },
      }
   })
);
